import React, { useState, useEffect } from 'react';
import { useOAuthCallback } from '../hooks/useAuth.js';
import { validateGoogleConfig } from '../config/google.js';

const BRAND_BLUE = '#4841F0';

const Login = ({ onLogin }) => {
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { handleCallback, loading: callbackLoading, error: callbackError } = useOAuthCallback();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');

    if (error) {
      setError(`Authentication failed: ${error}`);
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    if (code && state) {
      handleCallback(code, state);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [handleCallback]);

  useEffect(() => {
    if (callbackError) {
      setError(callbackError);
    }
  }, [callbackError]);

  const handleGoogleLogin = async () => {
    setError('');
    setIsLoading(true);

    try {
      if (!validateGoogleConfig()) {
        throw new Error('Google OAuth is not properly configured. Please check your environment variables.');
      }
      const { initiateGoogleLogin } = await import('../utils/auth.js');
      initiateGoogleLogin();
    } catch (err) {
      console.error('Google login error:', err);
      setError(err.message || 'Failed to initiate Google login');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{background: BRAND_BLUE}}>
      {/* Header bar */}
      <div className="px-8 py-5">
        <span style={{
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 800,
          fontSize: '2rem',
          color: '#ffffff',
          letterSpacing: '-0.02em',
        }}>
          Satellite
        </span>
      </div>

      {/* Hero content */}
      <div className="flex flex-1 flex-col items-center justify-center px-4 pb-16">
        <div className="text-center mb-10">
          <h1 style={{
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 800,
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            color: '#ffffff',
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
            marginBottom: '1rem',
          }}>
            Hiring Insights
          </h1>
          <p style={{
            color: 'rgba(255,255,255,0.8)',
            fontSize: '1.1rem',
            fontFamily: "'Inter', sans-serif",
          }}>
            See what's working in your hiring - and what's not!
          </p>
        </div>

        {/* Sign-in card */}
        <div style={{
          background: '#ffffff',
          borderRadius: '16px',
          padding: '2rem',
          width: '100%',
          maxWidth: '380px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
        }}>
          <h2 style={{
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 700,
            fontSize: '1.5rem',
            color: '#231f20',
            marginBottom: '0.5rem',
          }}>
            Sign in
          </h2>
          <p style={{color: '#6b7280', fontSize: '0.875rem', marginBottom: '1.5rem'}}>
            Access your Satellite Talent workspace
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <div className="flex">
                <svg className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-red-800 ml-3">{error}</p>
              </div>
            </div>
          )}

          {callbackLoading && (
            <div className="rounded-lg p-3 mb-4 flex items-center" style={{background: '#f0f0fe', border: '1px solid #c7c5fb'}}>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 mr-2" style={{borderColor: BRAND_BLUE}}></div>
              <p className="text-sm" style={{color: BRAND_BLUE}}>Completing authentication...</p>
            </div>
          )}

          <button
            type="button"
            onClick={handleGoogleLogin}
            disabled={isLoading || callbackLoading}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0.875rem 1.5rem',
              borderRadius: '10px',
              border: '1.5px solid #e5e7eb',
              background: '#ffffff',
              fontSize: '0.9375rem',
              fontWeight: 600,
              color: '#231f20',
              cursor: isLoading || callbackLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading || callbackLoading ? 0.6 : 1,
              transition: 'all 0.2s',
              fontFamily: "'Inter', sans-serif",
            }}
            onMouseEnter={e => { if (!isLoading && !callbackLoading) e.currentTarget.style.borderColor = BRAND_BLUE; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; }}
          >
            {isLoading || callbackLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500 mr-3"></div>
                {callbackLoading ? 'Completing sign in...' : 'Redirecting to Google...'}
              </>
            ) : (
              <>
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Sign in with Google Workspace
              </>
            )}
          </button>

          <p className="text-center text-xs mt-4" style={{color: '#9ca3af'}}>
            Only authorized Satellite Talent users can access this system
          </p>
        </div>

        <p className="text-xs mt-6" style={{color: 'rgba(255,255,255,0.5)'}}>
          Contact your administrator if you need access
        </p>
      </div>

      {/* Footer with logo */}
      <div className="px-8 py-6 text-center">
        <img 
          src="/logo.svg" 
          alt="Satellite Talent Logo" 
        />
      </div>
    </div>
  );
};

export default Login;
